import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { equipmentService, categoryService, branchService, regionService } from '../../services/api';
import { Card, Table, Pagination, StatusToggle, PageHeader, Modal, Select, NormalMultiSelect, Button, useToast } from '../../components/common';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';

// BioMedical equipment master. Follows the same shape as the other masters
// (BranchManager, CategoryManager) — Card + PageHeader + Table + Pagination —
// so it behaves and looks identical to the rest of the admin section.
// IT's equivalent is Tags, a different table with different columns.
const EquipmentManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();

  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  // Entity and branch are multi-select: an asset list is usually read a few
  // entities at a time, and the branch list narrows to the chosen entities.
  const [entityCodes, setEntityCodes] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [entities, setEntities] = useState([]);

  // Bulk re-filing. selectedIds holds ticked rows; allMatching means "every
  // record the current filter returns", including the pages not on screen.
  const [selectedIds, setSelectedIds] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');
  const [applying, setApplying] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 1, totalItems: 0, itemsPerPage: 30 });

  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    setPageTitle('Equipment');
  }, [setPageTitle]);

  useEffect(() => {
    fetchEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, categoryCode, entityCodes, branchIds, itemsPerPage]);

  useEffect(() => {
    categoryService.getAll({ dropdown: true })
      .then((r) => setCategories(Array.isArray(r.data) ? r.data : r.data.data || []))
      .catch(() => setCategories([]));
    regionService.getAll({ noPagination: 'true' })
      .then((r) => setEntities(r.data.data || []))
      .catch(() => setEntities([]));
  }, []);

  // Branches follow the chosen entities. With none chosen the list is every
  // branch, so a branch can still be picked on its own.
  useEffect(() => {
    const params = { noPagination: 'true' };
    if (entityCodes.length > 0) params.region_codes = entityCodes.join(',');
    branchService.getAll(params)
      .then((r) => {
        const rows = r.data.data || [];
        setBranches(rows);
        // Drop any branch that the new entity choice no longer offers
        setBranchIds((prev) => prev.filter((id) => rows.some((b) => String(b.id) === String(id))));
      })
      .catch(() => setBranches([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityCodes]);

  // The filters the list is showing. The bulk change sends the same set, so
  // "all matching" can only ever mean the rows on screen.
  const activeFilters = () => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (categoryCode) params.category_code = categoryCode;
    if (entityCodes.length > 0) {
      const ids = entities.filter((e) => entityCodes.includes(e.code)).map((e) => e.id);
      if (ids.length) params.region_ids = ids.join(',');
    }
    if (branchIds.length > 0) params.branch_ids = branchIds.join(',');
    return params;
  };

  const fetchEquipment = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage, ...activeFilters() };

      const response = await equipmentService.getAll(params);
      setEquipment(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch equipment');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // A filter change makes the old selection meaningless, so it is cleared.
  const clearSelection = () => {
    setSelectedIds([]);
    setAllMatching(false);
  };

  const toggleRow = (id) => {
    setAllMatching(false);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const pageIds = equipment.map((e) => e.id);
  const pageAllTicked = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const togglePage = () => {
    setAllMatching(false);
    setSelectedIds((prev) => (pageAllTicked
      ? prev.filter((id) => !pageIds.includes(id))
      : [...new Set([...prev, ...pageIds])]));
  };

  const selectedCount = allMatching ? pagination.totalItems : selectedIds.length;

  const handleApplyCategory = async () => {
    if (!bulkCategory || selectedCount === 0) return;
    const name = categories.find((c) => c.code === bulkCategory)?.name || bulkCategory;
    const confirmed = await toast.confirm(
      `Move ${selectedCount} equipment record${selectedCount === 1 ? '' : 's'} to "${name}"?`
    );
    if (!confirmed) return;

    try {
      setApplying(true);
      const body = allMatching
        ? { category_code: bulkCategory, all: true }
        : { category_code: bulkCategory, ids: selectedIds };
      // With all=true the filters go as query params, so the server picks the
      // same rows the screen is showing.
      const res = await equipmentService.bulkCategory(body, allMatching ? activeFilters() : undefined);
      toast.success(res.data.message);
      clearSelection();
      setBulkCategory('');
      fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update the category');
    } finally {
      setApplying(false);
    }
  };

  const handleItemsPerPageChange = (n) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const handleView = (row) => navigate(`/admin/equipment/view/${row.id}`);
  const handleEdit = (row) => navigate(`/admin/equipment/edit/${row.id}`);
  const handleAdd = () => navigate('/admin/equipment/create');

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this equipment?');
    if (!confirmed) return;
    try {
      await equipmentService.delete(id);
      toast.success('Equipment deleted successfully');
      fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete equipment');
    }
  };

  const handleStatusToggle = async (row, newStatus) => {
    setUpdatingStatus((prev) => ({ ...prev, [row.id]: true }));
    try {
      await equipmentService.updateStatus(row.id, newStatus);
      toast.success(`Equipment ${newStatus ? 'activated' : 'deactivated'} successfully`);
      await fetchEquipment();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [row.id]: false }));
    }
  };

  // Master history is recorded by logMasterHistory('equipment') on every
  // create, update, status change and delete.
  const handleViewHistory = async (row) => {
    try {
      setHistoryLoading(true);
      setHistoryFor(row);
      setShowHistoryModal(true);
      const response = await api.get(`/master-history/equipment/${row.id}`);
      setHistoryRows(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching equipment history:', err);
      setHistoryRows([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const form = new FormData();
    form.append('file', file);

    try {
      const res = await equipmentService.upload(form);
      toast.success(res.data.message);
      fetchEquipment();
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.error || 'Import failed');
      // Row-level reasons are too long for a toast, so surface them inline.
      setError(data?.details?.length ? `${data.error}\n${data.details.join('\n')}` : '');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  // Downloads what the filters are showing, in the upload file's columns, so a
  // corrected download can go straight back in through Upload.
  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await equipmentService.exportCsv(activeFilters());
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `equipment-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The refusal arrives as a blob, because the request asked for one
      let message = 'Could not download the equipment list';
      try {
        const text = await err.response?.data?.text?.();
        if (text) message = JSON.parse(text).error || message;
      } catch { /* keep the default */ }
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/equipment/template', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equipment-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the template');
    }
  };

  const mono = { fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: '500', color: '#374151' };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          addButton={{
            label: 'Add Equipment',
            onClick: handleAdd,
            variant: 'primary'
          }}
          customActions={
            <>
              {/* Icon-only, matching the BioMedical prototype's toolbar */}
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                title="Download the list as shown"
                aria-label="Download the list as shown"
                className={`inline-flex items-center justify-center w-10 h-10 border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:text-primary-600 transition-colors ${downloading ? 'opacity-60 pointer-events-none' : ''}`}
              >
                {downloading ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                    <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <rect x="7" y="9" width="3" height="8" />
                    <rect x="13" y="5" width="3" height="12" />
                  </svg>
                )}
              </button>

              <button
                type="button"
                onClick={handleDownloadTemplate}
                title="Download CSV template"
                aria-label="Download CSV template"
                className="inline-flex items-center justify-center w-10 h-10 border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:text-primary-600 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              </button>

              <label
                title="Upload CSV"
                aria-label="Upload CSV"
                className={`inline-flex items-center justify-center w-10 h-10 border border-gray-300 rounded-md text-gray-600 bg-white hover:bg-gray-50 hover:text-primary-600 transition-colors cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
              >
                {uploading ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                    <circle cx="12" cy="12" r="9" strokeOpacity="0.25" />
                    <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                )}
                <input
                  ref={fileInput}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            </>
          }
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search equipment, manufacturer, model, serial...'
            },
            dropdowns: [
              {
                value: categoryCode,
                placeholder: 'All Categories',
                onChange: (e) => {
                  setCategoryCode(e?.target ? e.target.value : e);
                  setCurrentPage(1);
                  clearSelection();
                },
                options: [
                  { value: '', label: 'All Categories' },
                  ...categories.map((c) => ({ value: c.code, label: c.name }))
                ]
              }
            ],
            // Entity and branch are multi-select, so they go here rather than
            // in the shared single-select dropdown row.
            custom: (
              <>
                <div className="w-full sm:w-[220px]">
                  <NormalMultiSelect
                    name="entities"
                    value={entityCodes}
                    onChange={(e) => {
                      setEntityCodes(e.target.value);
                      setCurrentPage(1);
                      clearSelection();
                    }}
                    options={entities.map((r) => ({ value: r.code, label: r.name }))}
                    placeholder="All Entities"
                    className="mb-0"
                  />
                </div>
                <div className="w-full sm:w-[220px]">
                  <NormalMultiSelect
                    name="branches"
                    value={branchIds}
                    onChange={(e) => {
                      setBranchIds(e.target.value);
                      setCurrentPage(1);
                      clearSelection();
                    }}
                    options={branches.map((b) => ({ value: String(b.id), label: b.name }))}
                    placeholder={entityCodes.length ? 'Branches of the selected entities' : 'All Branches'}
                    className="mb-0"
                  />
                </div>
              </>
            )
          }}
        />

        {/* Re-filing bar: appears only once rows are ticked */}
        {selectedCount > 0 && (
          <div className="mb-3 px-3 py-2 rounded-md border border-primary-200 bg-primary-50 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-primary-800">
              {selectedCount} selected
            </span>

            {!allMatching && pageAllTicked && pagination.totalItems > selectedIds.length && (
              <button
                type="button"
                className="text-sm text-primary-700 underline hover:text-primary-900"
                onClick={() => setAllMatching(true)}
              >
                Select all {pagination.totalItems} matching these filters
              </button>
            )}
            {allMatching && (
              <span className="text-sm text-primary-700">
                every record matching these filters
              </span>
            )}

            <div className="w-full sm:w-[200px]">
              <Select
                value={bulkCategory}
                onChange={(e) => setBulkCategory(e?.target ? e.target.value : e)}
                options={[
                  { value: '', label: 'Move to category...' },
                  ...categories.map((c) => ({ value: c.code, label: c.name }))
                ]}
                placeholder="Move to category..."
                className="mb-0"
                style={{ marginBottom: 0 }}
              />
            </div>

            <Button
              variant="primary"
              onClick={handleApplyCategory}
              disabled={!bulkCategory || applying}
            >
              {applying ? 'Applying...' : 'Apply'}
            </Button>
            <button
              type="button"
              className="text-sm text-gray-600 hover:text-gray-900"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
        )}

        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600 whitespace-pre-line text-sm">
            {error}
          </div>
        )}

        <Table
          columns={[
            {
              // Ticking rows is for re-filing them in bulk; the header box
              // takes the whole page, and the bar above offers every match.
              header: (
                <input
                  type="checkbox"
                  checked={pageAllTicked}
                  onChange={togglePage}
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Select all rows on this page"
                  className="h-4 w-4 cursor-pointer accent-primary-600"
                />
              ),
              accessor: 'select',
              width: '44px',
              align: 'center',
              render: (_, row) => (
                <input
                  type="checkbox"
                  checked={allMatching || selectedIds.includes(row.id)}
                  onChange={() => toggleRow(row.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select ${row.name}`}
                  className="h-4 w-4 cursor-pointer accent-primary-600"
                />
              )
            },
            {
              header: 'S.No',
              accessor: 'serial',
              width: '70px',
              render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
            },
            {
              header: 'Code',
              accessor: 'code',
              width: '120px',
              render: (value) => <span style={mono}>{value}</span>
            },
            {
              header: 'Name of the equipment',
              accessor: 'name',
              width: '220px',
              render: (value) => (
                <span className="text-sm text-gray-900" title={value}>
                  {value && value.length > 35 ? `${value.substring(0, 35)}...` : value}
                </span>
              )
            },
            {
              header: 'Category',
              accessor: 'category_name',
              width: '150px',
              render: (value) => value
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200">{value}</span>
                : <span className="text-gray-400">—</span>
            },
            { header: 'Entity', accessor: 'region_name', width: '140px', render: (v) => v || '—' },
            { header: 'Branch', accessor: 'branch_name', width: '140px', render: (v) => v || '—' },
            { header: 'Manufacturer', accessor: 'manufacturer', width: '160px', render: (v) => v || '—' },
            { header: 'Model', accessor: 'model', width: '130px', render: (v) => v || '—' },
            {
              header: 'Serial number',
              accessor: 'serial_number',
              width: '140px',
              render: (v) => v ? <span style={mono}>{v}</span> : <span className="text-gray-400">—</span>
            },
            {
              header: 'Created',
              accessor: 'created_at',
              width: '110px',
              render: (value) => formatDateDDMMYYYY(value)
            },
            {
              header: 'Status',
              accessor: 'status',
              width: '100px',
              align: 'center',
              render: (value, row) => (
                <StatusToggle
                  status={value}
                  onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                  loading={updatingStatus[row.id]}
                />
              )
            },
            {
              header: 'Actions',
              accessor: 'actions',
              width: '120px',
              align: 'center',
              actions: [
                { type: 'history', onClick: handleViewHistory },
                { type: 'edit', onClick: handleEdit },
                { type: 'delete', onClick: (row) => handleDelete(row.id) }
              ]
            }
          ]}
          data={equipment}
          onRowClick={handleView}
          emptyMessage="No equipment found"
          loading={loading}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </Card>

      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
        <div className="p-6 max-w-3xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Equipment History</h2>
              {historyFor && (
                <p className="text-sm text-gray-500 mt-0.5">
                  {historyFor.code} &middot; {historyFor.name}
                </p>
              )}
            </div>
            <button
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
              onClick={() => setShowHistoryModal(false)}
            >
              &times;
            </button>
          </div>

          {historyLoading && <p className="text-gray-500 py-6">Loading history…</p>}

          {!historyLoading && historyRows.length === 0 && (
            <p className="text-gray-500 py-6">No changes recorded for this equipment yet.</p>
          )}

          {!historyLoading && historyRows.length > 0 && (
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
              {historyRows.map((h, i) => (
                <div key={h.id || i} className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-medium text-gray-900 capitalize">
                      {h.action_type || 'update'}
                    </span>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {h.performed_at ? new Date(h.performed_at).toLocaleString() : ''}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    by {h.performed_by_name || h.performed_by || 'Unknown'}
                    {h.status_code && h.status_code >= 400 && (
                      <span className="ml-2 text-danger-600">failed ({h.status_code})</span>
                    )}
                  </p>
                  {h.request_data && Object.keys(h.request_data).length > 0 && (
                    <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                      {JSON.stringify(h.request_data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default EquipmentManager;
